import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import anime from 'animejs';
import InfiniteScroll from 'react-infinite-scroll-component';
import { DEFAULT_INFINITE_SCROLL_CONFIG } from '../../../utils/infiniteScrollUtils';
import cancelIcon from '../../../assets/images/close-icon.svg';
import dataModel from '../../../models/DataModel';
import LoadingDots from '../../../components/LoadingDots';
import SelectProjectList from './SelectProjectList';
import useAvailableProjectsPicker from './useAvailableProjectsPicker';
import './AddProjectGroupPopup.scss';
import Input from '../../../components/Input';
import projectGroupService from '../../../services/projectGroupService';
import ProjectSearchFilter from '../../../components/ProjectsNav/ProjectSearchFilter';
import TextMap from '../../../helpers/TextMap';

const SCROLL_TARGET_ID = 'available-projects-scroll';

const AddProjectGroupPopup = ({ handleClosePopup, currentCompany, handleAddGroup }) => {
  const textMap = new TextMap();
  const deactivatePopupRef = useRef(null);
  const inputFieldRef = useRef(null);

  const {
    projects,
    loadMoreProjects,
    handleSearchChange,
    hasMore,
  } = useAvailableProjectsPicker();

  const [groupedProject, setGroupedProject] = useState([]);
  const [projectGroupName, setProjectGroupName] = useState('');
  const [isSaveActive, setIsSaveActive] = useState(false);

  useEffect(() => {
    dataModel.setDisableAppBackground(true);
    if (inputFieldRef.current) inputFieldRef.current.focus();
    return () => dataModel.setDisableAppBackground(false);
  }, []);

  const closePopup = useCallback(() => {
    dataModel.setDisableAppBackground(false);
    if (handleClosePopup) handleClosePopup();
  }, [handleClosePopup]);

  const collapsePopup = () => {
    anime({
      targets: deactivatePopupRef.current,
      height: 0,
      easing: 'easeInOutCubic',
      duration: 200,
      complete: closePopup,
    });
  };

  const handleGroupNameChange = (e) => {
    const value = e.target.value;
    setProjectGroupName(value);
    setIsSaveActive(value.length > 0);
  };

  const handleSave = () => {
    if (!projectGroupName || !currentCompany || !currentCompany.id) return;
    const projectIds = groupedProject.map(p => p.id);
    projectGroupService
      .createProjectGroup(currentCompany.id, projectGroupName, projectIds)
      .then(response => {
        if (!response.error && handleAddGroup) {
          handleAddGroup(projectIds);
          closePopup();
        }
      })
      .catch(() => {
        dataModel.displayWarnErrorMessage('Failed to save project group', 3000, 2);
      });
  };

  const handleAddProject = useCallback((project) => {
    setGroupedProject(prev =>
      prev.some(p => p.id === project.id) ? prev : [...prev, project]
    );
  }, []);

  const handleRemoveProject = useCallback((project) => {
    setGroupedProject(prev => prev.filter(p => p.id !== project.id));
  }, []);

  const selectedIds = useMemo(() => new Set(groupedProject.map(p => p.id)), [groupedProject]);

  const selectedProjectList = useMemo(
    () =>
      [...groupedProject].sort((a, b) =>
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      ),
    [groupedProject]
  );

  const availableProjectList = useMemo(
    () =>
      projects
        .filter(p => p && !p.project_group_id && !selectedIds.has(p.id))
        .sort((a, b) =>
          (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
        ),
    [projects, selectedIds]
  );

  return (
    <div className="add-project-group-background">
      <div className="add-project-group" ref={deactivatePopupRef}>
        <header className="add-project-group__header">
          <div className="caption">New Project Group</div>
          <div className="close-button" onClick={collapsePopup}>
            <img src={cancelIcon} alt="cancel" />
          </div>
        </header>

        <div className="add-project-group__container">
          <div className="project-group-input-name">
            <div>
              <label>Project Group Name:</label>
            </div>
            <Input
              onChange={handleGroupNameChange}
              value={projectGroupName}
              ref={inputFieldRef}
              placeholder={textMap.projectGroupName}
            />
          </div>
          <div className="project-group-selection">
            <div className="left-side">
              <div className="selection-section project-section">
                <div className="pane-caption">Available Projects</div>
                <div className="project-groups-search">
                  <ProjectSearchFilter
                    handleSearchChange={handleSearchChange}
                    overridePlaceHolder={textMap.searchAvailableProjects}
                    hideFilter={true}
                  />
                </div>
                <div id={SCROLL_TARGET_ID} className="available-projects-scroll">
                  <InfiniteScroll
                    scrollableTarget={SCROLL_TARGET_ID}
                    dataLength={availableProjectList.length}
                    next={loadMoreProjects}
                    hasMore={hasMore}
                    loader={
                      <div className="loading-container">
                        <LoadingDots />
                      </div>
                    }
                    endMessage={null}
                    scrollThreshold={DEFAULT_INFINITE_SCROLL_CONFIG.scrollThreshold}
                  >
                    <SelectProjectList
                      projects={availableProjectList}
                      selectProject={handleAddProject}
                      type="available"
                    />
                  </InfiniteScroll>
                </div>
                <div className="instructions"><i>Note:</i> Click to Add</div>
              </div>
            </div>
            <div className="right-side">
              <div className="selection-section project-section">
                <div className="pane-caption">Selected Projects</div>
                <SelectProjectList
                  projects={selectedProjectList}
                  selectProject={handleRemoveProject}
                  height={280}
                />
                <div className="instructions"><i>Note:</i> Click to Remove</div>
              </div>
            </div>
          </div>
        </div>

        <div className="add-project-group__button-container">
          <button className="cancel-button" onClick={collapsePopup}>CANCEL</button>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={!isSaveActive}
          >
            SAVE
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentCompany: state.new_companies.currentCompany,
});

export default withRouter(connect(mapStateToProps)(AddProjectGroupPopup));



// WEBPACK FOOTER //
// ./src/containers/ProjectSummaryContainer/AddProjectGroupPopup/index.js